/**
 * GLDN shared cart snapshot (gldn95 #405)
 * =======================================
 *
 * One coalesced `/cart.js` read shared by every GLDN cart script.
 *
 * The problem: gldn-gift-box, gldn-gift-note, gldn-cart-orphan-cleanup and
 * gldn-cart-atc-instrument each fetched `/cart.js` independently on page load,
 * on their own schedules (DOMContentLoaded, requestIdleCallback, render passes,
 * init retries). A measured homepage load issued 17 `/cart.js` requests inside
 * 3.6s — all returning the same body. gldn-gift-box already solved this
 * privately with an in-flight + short-TTL cache; this generalises that so the
 * coalescing works ACROSS scripts instead of within one.
 *
 * Contract:
 *   window.gldnCartSnapshot(force?)      -> Promise<cart>
 *   window.gldnCartSnapshot.invalidate() -> void
 *
 * Semantics:
 *   - At most ONE request in flight. Concurrent callers share it.
 *   - A snapshot newer than TTL_MS is served without a network call.
 *   - `force: true` discards the cache AND any in-flight read, then starts a
 *     fresh one. Post-write callers must use it — a read already in flight when
 *     the write landed can return pre-write state.
 *   - Any cart mutation event invalidates the cache, so the TTL can never serve
 *     a stale cart across a change.
 *
 * Every caller keeps a direct-fetch fallback, so this file failing to load
 * degrades to the previous behaviour rather than breaking the cart.
 *
 * NOTE ON `fetch`: the native fetch is captured at load. Several scripts (and
 * apps — Beam, Okendo, Shopify's own listener) wrap window.fetch to observe
 * cart writes. This is a read, so bypassing those wrappers is both safe and
 * desirable: it keeps our polling reads out of other people's interceptors and
 * avoids re-entering gldn-cart-orphan-cleanup's own wrapper. This file must
 * therefore be loaded BEFORE the other gldn-cart-* scripts (they are all
 * `defer`, which preserves document order).
 */
(function () {
  'use strict';

  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  var TTL_MS = 1000;

  var cache = null;
  var cacheAt = 0;
  var inFlight = null;

  function read() {
    return nativeFetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res || !res.ok) throw new Error('cart.js responded ' + (res && res.status));
      return res.json();
    });
  }

  function invalidate() {
    cache = null;
    cacheAt = 0;
  }

  function snapshot(force) {
    if (!nativeFetch) return Promise.reject(new Error('fetch unavailable'));

    if (force) {
      // Drop the in-flight read too: it may have been issued before the write
      // that prompted this call, so its body could be pre-write.
      invalidate();
      inFlight = null;
    } else {
      if (cache && (Date.now() - cacheAt) < TTL_MS) return Promise.resolve(cache);
      if (inFlight) return inFlight;
    }

    var pending = read().then(function (cart) {
      cache = cart;
      cacheAt = Date.now();
      if (inFlight === pending) inFlight = null;
      return cart;
    }).catch(function (err) {
      if (inFlight === pending) inFlight = null;
      throw err;
    });

    inFlight = pending;
    return pending;
  }

  // Any cart mutation invalidates the snapshot, so the TTL can never serve a
  // cart from before a change. Horizon dispatches cart:update; the others are
  // defensive for theme/app-dispatched variants.
  ['cart:update', 'cart:refresh', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, invalidate);
  });

  snapshot.invalidate = invalidate;
  window.gldnCartSnapshot = snapshot;
})();
